import { useQuery } from '@tanstack/react-query';
import { getCurrentUser, getPermissions } from '../api/client';

export interface CurrentUser {
  id: number;
  azure_ad_id: string | null;
  email: string;
  name: string;
  display_name?: string | null;
  role: string;
  team: string | null;
  display_title: string | null;
  active: boolean;
  moneta_enabled: boolean;
  can_review_all_billing: boolean;
  is_billing_clerk: boolean;
  ai_tools_enabled: boolean;
  dropbox_id: string | null;
  todoist_id: string | null;
  // Tasks-SoT PR C, Task 5 (AM-C5): this hook's `CurrentUser` is a
  // hand-maintained subset of `UserFull` (typesCaseCore.ts), not a re-export
  // of it — optional (like the other fields below added after this
  // interface's original shape) so existing fixtures that predate the
  // field don't need updating just to keep compiling.
  todoist_mirror_opt_out?: boolean;
  // P3-A12: per-user opt-out for the urgent-task-assignment push
  // notification — optional, same reasoning as todoist_mirror_opt_out above.
  buzz_urgent_tasks?: boolean;
  // P3-A5: server-side install-coach dismissal timestamp (source of truth;
  // localStorage is just a same-device fast-path cache — see InstallCoach.tsx).
  a2hs_coach_dismissed_at?: string | null;
  billable_rate_cents: number | null;
  ringcentral_extension: string | null;
  m365_mailbox: string | null;
  email_signature: string | null;
  email_signature_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  auth_provider?: 'microsoft' | 'native';
}

export function useCurrentUser() {
  return useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function usePermissions() {
  return useQuery<Record<string, string[]>>({
    queryKey: ['permissions'],
    queryFn: getPermissions,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useHasPermission(permission: string): boolean {
  const { data: user } = useCurrentUser();
  const { data: permMap } = usePermissions();
  if (!user || !permMap) return false;
  const perms = permMap[user.role] ?? [];
  return perms.includes(permission);
}

export function useIsAdmin(): boolean {
  const { data: user } = useCurrentUser();
  return user?.role === 'admin';
}
